import { useEffect, useRef } from 'react';
import styled from 'styled-components';

import { ListingType } from 'algolia-api/types/ListingType';
import { ApiFilestackImageData } from 'api/types/ApiFilestackImageData';
import { Box } from 'components/Box';
import { PrimaryButton } from 'components/Button';
import { Hero } from 'components/Hero';
import { RouterNavLink } from 'components/Link';
import { H1 } from 'components/Text/H1';
import { RichText } from 'components/Text/RichText';
import { useSticky } from 'hooks/viewport/useSticky';
import { useViewportBreakpointValue } from 'hooks/viewport/useViewportBreakpointValue';
import {
  colors,
  cssBreakpoints,
  fontSizesByType,
  gutterWidth,
  maxWidth,
} from 'theme';
import { useAmpContext } from 'utils/AmpContext';
import { LISTING_TYPE_HUMAN_NAME_MAP_SEARCH } from 'utils/constants';
import { listingSearchHome } from 'utils/searchHomePages';

import { SearchInputs } from './SearchInputs';
import { useMainSearchMenu } from './useMainSearchMenu';

const Title = styled.div`
  line-height: 1.3;
  font-size: ${fontSizesByType.h3[0]}px;
  @media all and (min-width: ${cssBreakpoints.smUp}) {
    font-size: ${fontSizesByType.h3[2]}px;
  }
`;

type Props = {
  title?: string;
  image: ApiFilestackImageData;
  imageMobile: ApiFilestackImageData | undefined;
  onSearch: (params: {
    query?: string;
    type?: string;
    searchType?: string;
  }) => void;
  searchMode?: boolean;
  defaultSearchType?: ListingType;
  setShowStickyMenu: (showStickyMenu: boolean) => void;
};

export function SearchHero({
  title,
  image,
  imageMobile,
  onSearch,
  searchMode,
  defaultSearchType,
  setShowStickyMenu,
}: Props) {
  const isAmp = useAmpContext();
  const mainProps = useMainSearchMenu({
    searchFrom: 'Search Hero',
    searchMode,
    defaultSearchType,
  });

  const offsetTop = useViewportBreakpointValue({
    lg: 0,
  });

  const searchHeroRef = useRef<HTMLDivElement>(null);
  const menuShouldBeSticky = useSticky({
    ref: searchHeroRef,
    offset: { top: offsetTop },
    onlyIfScrolledPast: true,
  });

  useEffect(() => {
    if (menuShouldBeSticky) {
      setShowStickyMenu(true);
    } else {
      setShowStickyMenu(false);
    }
  }, [menuShouldBeSticky, setShowStickyMenu]);

  const ampDefaultSearchType = defaultSearchType || 'JOB';

  return (
    <div ref={searchHeroRef}>
      <Hero
        pb={[36, null, null, 64]}
        pt={[36, null, null, 80]}
        minHeight={['470px', null, null, 'calc(100vh - 300px)']}
        heroImage={image}
        mobileHeroImage={imageMobile}
        eagerLoadImage
        data-qa-id="search-hero"
      >
        {!searchMode && (
          <Box
            data-qa-id="search-hero-menu"
            width="100%"
            maxWidth={[400, null, null, maxWidth[3]]}
            px={[0, null, null, gutterWidth]}
            m="0 auto"
          >
            <Box
              width={[1, null, null, 1 / 2]}
              px={[20, null, null, 60]}
              py={[20, null, null, 40]}
              borderRadius={12}
              boxShadow="0 0 6px rgba(0,0,0,0.3)"
              bg="white"
              color={colors.mediumContentGrey}
            >
              <Box mb={20}>
                <RichText>
                  <H1>
                    <Title
                      dangerouslySetInnerHTML={{
                        __html:
                          title?.replace(/<(\/?)(p|h1)>/g, '<$1span>') || '',
                      }}
                    />
                  </H1>
                </RichText>
              </Box>

              {isAmp ? (
                <RouterNavLink
                  to={{
                    pathname: listingSearchHome(ampDefaultSearchType),
                  }}
                  state={{
                    filterType: ampDefaultSearchType,
                  }}
                >
                  <PrimaryButton>
                    {`${getText('Search')} ${
                      LISTING_TYPE_HUMAN_NAME_MAP_SEARCH[ampDefaultSearchType]
                    }`}
                  </PrimaryButton>
                </RouterNavLink>
              ) : (
                <SearchInputs
                  onSearch={onSearch}
                  prefixVariant="transparent"
                  styleVariant="light-border"
                  selectedType={mainProps.selectedType}
                  listingTypes={mainProps.listingTypes}
                  clickLogin={mainProps.clickLogin}
                  showLogin={mainProps.showLogin}
                  clearSearchInput={mainProps.clearSearchInput}
                  search={mainProps.search}
                  query={mainProps.query}
                  searchLocation={mainProps.searchLocation}
                  setListingType={mainProps.setListingType}
                  setQuery={mainProps.setUserQuery}
                  onLocationChange={mainProps.onLocationChange}
                  redirectParam={mainProps.redirectParam}
                />
              )}
            </Box>
          </Box>
        )}
      </Hero>
    </div>
  );
}
